import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { Input, PhoneInput, isValidPhoneNumber } from '@column/ui';

import { PEP_STATUS_OPTIONS } from '../_util/entityOptions';
import type { PersonEntityFormValues } from '../_util/useEntityForm';
import { RenderFields } from '~/components';
import { SectionField, SectionWithFields } from '~/components/RenderFields';
import { useAddress } from '~/hooks';

import {
  CountrySelect,
  DateTextInput,
  EntityAddressFields,
  NumberListInput,
  OptionSelect,
  PaymentRailsInput,
  StringListInput,
  YesNoInput,
  validateDate,
} from './EntityFieldInputs';
import { PersonIdentificationFields } from './PersonIdentificationFields';

// Seed the "Is this person a U.S. Citizen?" toggle: default new people to citizen, and for an
// existing person infer it from whichever identification document is already on file.
const useIsCitizenDefaults = (form: ReturnType<typeof useFormContext>, hasId: boolean) => {
  const isCitizen = form.watch('temporaryData.isCitizen');
  const ssn = form.watch('ssn');
  const passport = form.watch('passport.number');
  const driversLicense = form.watch('driversLicense.number');
  const nationalId = form.watch('nationalId.number');

  useEffect(() => {
    if (typeof isCitizen !== 'boolean') {
      if (!hasId) {
        form.setValue('temporaryData.isCitizen', true);
        return;
      }
      if (ssn) {
        form.setValue('temporaryData.isCitizen', true);
      } else if (passport || driversLicense || nationalId) {
        form.setValue('temporaryData.isCitizen', false);
      } else {
        form.setValue('temporaryData.isCitizen', true);
      }
    }
  }, [form, hasId, isCitizen, ssn, passport, driversLicense, nationalId]);
};

export interface PersonEntityFieldsProps {
  // The id of an existing person when editing; absent while creating. Drives the citizen default
  // and whether the SSN input starts revealed.
  id?: string;
  disableForm?: boolean;
  // The full person page shows contact fields (middle name, email, phone); the create-and-link
  // modal omits them to stay lean. The shared field definitions are otherwise identical.
  includeContactFields?: boolean;
  // Headline for the leading section (the page labels it "Information"; the modal renders it bare).
  informationHeadline?: string;
}

// The person-entity form fields, shared by the full Person Entity page and the create-and-link
// modal so their inputs, validation rules, and layout can never drift apart.
export const PersonEntityFields: React.FC<PersonEntityFieldsProps> = ({
  id,
  disableForm,
  includeContactFields,
  informationHeadline,
}) => {
  const form = useFormContext();
  const { countries } = useAddress({ types: ['country'] });

  useIsCitizenDefaults(form, Boolean(id));

  // Undefined until a default or loaded entity answers it, so neither ID group shows yet.
  const isCitizen = form.watch('temporaryData.isCitizen') as boolean | undefined;
  // Only the user's own answer animates the ID groups; defaults and loaded values show them in place.
  const [hasAnsweredCitizenship, setHasAnsweredCitizenship] = useState(false);
  const addressCountryCode = form.watch('address.countryCode') as string | undefined;

  const countryOptions = countries.map((c) => ({ label: c.name, value: c.code }));

  const middleName: SectionField<PersonEntityFormValues, undefined> = {
    id: 'middleName',
    label: 'Middle Name',
    children: ({ value, onChange, onBlur }) => (
      <Input
        className="w-full"
        value={(value as string) ?? ''}
        onChange={onChange}
        onBlur={onBlur}
        placeholder="Middle Name"
        disabled={disableForm}
      />
    ),
  };

  const contactFields: SectionField<PersonEntityFormValues, undefined>[] = [
    {
      id: 'email',
      label: 'Email Address',
      children: ({ value, onChange, onBlur }) => (
        <Input
          className="w-full"
          value={(value as string) ?? ''}
          onChange={onChange}
          onBlur={onBlur}
          placeholder="Email Address"
          disabled={disableForm}
        />
      ),
    },
    {
      id: 'phoneNumber',
      label: 'Phone Number',
      rules: {
        validate: (value) =>
          !value || isValidPhoneNumber(String(value)) || 'Please enter a valid phone number for the selected country',
      },
      children: ({ value, onChange, onBlur }, { error }) => (
        <PhoneInput
          value={(value as string) ?? ''}
          onChange={onChange}
          onBlur={onBlur}
          defaultCountry={addressCountryCode || 'US'}
          disabled={disableForm}
          aria-invalid={!!error}
        />
      ),
    },
  ];

  const informationSections: SectionWithFields<PersonEntityFormValues, undefined>[] = [
    {
      headline: informationHeadline,
      fields: [
        {
          id: 'firstName',
          label: 'First Name',
          rules: { required: true },
          children: ({ value, onChange, onBlur }, { error }) => (
            <Input
              className="w-full"
              value={(value as string) ?? ''}
              onChange={onChange}
              onBlur={onBlur}
              placeholder="First Name"
              disabled={disableForm}
              aria-invalid={!!error}
            />
          ),
        },
        ...(includeContactFields ? [middleName] : []),
        {
          id: 'lastName',
          label: 'Last Name',
          rules: { required: true },
          children: ({ value, onChange, onBlur }, { error }) => (
            <Input
              className="w-full"
              value={(value as string) ?? ''}
              onChange={onChange}
              onBlur={onBlur}
              placeholder="Last Name"
              disabled={disableForm}
              aria-invalid={!!error}
            />
          ),
        },
        {
          id: 'dateOfBirth',
          label: 'Date of Birth',
          rules: { required: true, validate: validateDate('Date of Birth') },
          children: ({ value, onChange, onBlur }, { error }) => (
            <DateTextInput
              value={(value as string) ?? ''}
              onChange={onChange}
              onBlur={onBlur}
              isDisabled={disableForm}
              hasError={!!error}
            />
          ),
        },
        ...(includeContactFields ? contactFields : []),
        {
          id: 'temporaryData.isCitizen',
          label: 'Is this person a U.S. Citizen?',
          children: ({ value, onChange }) => (
            <YesNoInput
              value={value}
              onChange={(next) => {
                setHasAnsweredCitizenship(true);
                onChange(next);
              }}
              isDisabled={disableForm}
            />
          ),
        },
        {
          content: (
            <PersonIdentificationFields
              isCitizen={isCitizen}
              animate={hasAnsweredCitizenship}
              countryOptions={countryOptions}
              disabled={disableForm}
              ssnDefaultVisible={!id}
            />
          ),
        },
      ],
    },
  ];

  const detailSections: SectionWithFields<PersonEntityFormValues, undefined>[] = [
    {
      headline: 'Employment & Income',
      isCollapsed: true,
      fields: [
        {
          id: 'employmentStatus',
          label: 'Employment Status',
          children: ({ value, onChange, onBlur }) => (
            <Input
              className="w-full"
              value={(value as string) ?? ''}
              onChange={onChange}
              onBlur={onBlur}
              placeholder="Employment Status"
              disabled={disableForm}
            />
          ),
        },
        {
          id: 'occupation',
          label: 'Occupation',
          children: ({ value, onChange, onBlur }) => (
            <Input
              className="w-full"
              value={(value as string) ?? ''}
              onChange={onChange}
              onBlur={onBlur}
              placeholder="Occupation"
              disabled={disableForm}
            />
          ),
        },
        {
          id: 'role',
          label: 'Role',
          description: "Individual's role or job title at their place of employment.",
          children: ({ value, onChange, onBlur }) => (
            <Input
              className="w-full"
              value={(value as string) ?? ''}
              onChange={onChange}
              onBlur={onBlur}
              placeholder="Role"
              disabled={disableForm}
            />
          ),
        },
        {
          id: 'sourceOfIncome',
          label: 'Source of Income',
          description: 'Comma-separated list.',
          children: ({ value, onChange }) => (
            <StringListInput
              value={value as string[] | undefined}
              onChange={onChange}
              placeholder="e.g. salary, investments"
              isDisabled={disableForm}
            />
          ),
        },
        {
          id: 'income',
          label: 'Estimated Annual Income',
          description: 'Comma-separated amounts to pass income ranges.',
          children: ({ value, onChange }) => (
            <NumberListInput
              value={value as number[] | undefined}
              onChange={onChange}
              placeholder="e.g. 50000, 100000"
              isDisabled={disableForm}
            />
          ),
        },
      ],
    },
    {
      headline: 'Account Activity',
      isCollapsed: true,
      fields: [
        {
          id: 'purposeOfAccount',
          label: 'Purpose of Account',
          description: 'Comma-separated list.',
          children: ({ value, onChange }) => (
            <StringListInput
              value={value as string[] | undefined}
              onChange={onChange}
              placeholder="Intended use of the account"
              isDisabled={disableForm}
            />
          ),
        },
        {
          id: 'expectedPaymentRails',
          label: 'Expected Payment Rails',
          children: ({ value, onChange }) => (
            <PaymentRailsInput value={value} onChange={onChange} isDisabled={disableForm} />
          ),
        },
        {
          id: 'makesInternationalPayments',
          label: 'Makes International Payments?',
          children: ({ value, onChange }) => <YesNoInput value={value} onChange={onChange} isDisabled={disableForm} />,
        },
      ],
    },
    {
      headline: 'Compliance',
      isCollapsed: true,
      fields: [
        {
          id: 'countryOfCitizenship',
          label: 'Country of Citizenship',
          children: ({ value, onChange }) => (
            <CountrySelect
              value={value as string | undefined}
              onChange={onChange}
              options={countryOptions}
              isDisabled={disableForm}
            />
          ),
        },
        {
          id: 'pepStatus',
          label: 'PEP Status',
          description: 'Politically Exposed Person screening status.',
          children: ({ value, onChange }) => (
            <OptionSelect
              value={value as string | undefined}
              onChange={onChange}
              options={PEP_STATUS_OPTIONS}
              placeholder="Select PEP status..."
              isDisabled={disableForm}
            />
          ),
        },
        {
          id: 'isHighRisk',
          label: 'High Risk?',
          children: ({ value, onChange }) => <YesNoInput value={value} onChange={onChange} isDisabled={disableForm} />,
        },
      ],
    },
  ];

  return (
    <>
      <RenderFields sections={informationSections} />
      <EntityAddressFields isDisabled={disableForm} />
      <RenderFields sections={detailSections} />
    </>
  );
};
